import type { AiServiceRow, SvcService } from "@/lib/api";

/** What the table shows: every row, or those in one state. */
export type RowFilter = "all" | "over_budget" | "at_80" | "suspended" | "direct" | "warning";

/** The table's filter choices, as an admin reads them. */
export const ROW_FILTERS: { value: RowFilter; label: string }[] = [
  { value: "all", label: "Every row" },
  { value: "over_budget", label: "Over budget" },
  { value: "at_80", label: "At 80 % or more" },
  { value: "suspended", label: "Suspended" },
  { value: "direct", label: "Models direct (no gateway)" },
  { value: "warning", label: "Has a warning" },
];

/** The columns the table sorts on. */
export type SortColumn = "project" | "service" | "status" | "used" | "last_used";

/** A row needs a look: a warning, an overdue Foundry role, unpriced use, or an unknown project. */
export function hasWarning(row: AiServiceRow): boolean {
  return (
    Boolean(row.warning) ||
    Boolean(row.foundry_access_overdue) ||
    Boolean(row.month_unpriced) ||
    row.project_id == null
  );
}

/** Whether *row* is shown under *filter*, *service* and the search text. */
export function rowMatches(
  row: AiServiceRow,
  filter: RowFilter,
  service: SvcService | "all",
  search: string,
): boolean {
  if (service !== "all" && row.service !== service) {
    return false;
  }
  const query = search.trim().toLowerCase();
  if (
    query &&
    ![row.project_name, row.project_slug, row.owner_email ?? "", row.owner_name ?? ""].some(
      (text) => text.toLowerCase().includes(query),
    )
  ) {
    return false;
  }
  switch (filter) {
    case "all":
      return true;
    case "over_budget":
      return Boolean(row.over_budget);
    case "at_80":
      return Boolean(row.at_80_percent);
    case "suspended":
      return Boolean(row.suspended);
    case "direct":
      return row.service === "llm" && row.llm_mode === "direct";
    case "warning":
      return hasWarning(row);
  }
}

/** *rows* sorted on *column*; ties keep project, then service, order. */
export function sortRows(
  rows: AiServiceRow[],
  column: SortColumn | null,
  direction: "asc" | "desc",
): AiServiceRow[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const primary = column ? compareOn(column, a, b) * sign : 0;
    return (
      primary || a.project_name.localeCompare(b.project_name) || a.service.localeCompare(b.service)
    );
  });
}

/** The share of the budget used, for sorting: rows without a budget sort lowest. */
export function usedPercent(row: AiServiceRow): number {
  return row.budget_used_percent ?? -1;
}

function compareOn(column: SortColumn, a: AiServiceRow, b: AiServiceRow): number {
  switch (column) {
    case "project":
      return a.project_name.localeCompare(b.project_name);
    case "service":
      return a.service.localeCompare(b.service);
    case "status":
      return a.status.localeCompare(b.status);
    case "used":
      return usedPercent(a) - usedPercent(b);
    case "last_used":
      return (a.last_used_at ?? "").localeCompare(b.last_used_at ?? "");
  }
}
